import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SuggestionJob, SuggestionRow } from '@nemo/api';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { AffinityCheckButton, useCandidateCheck } from '../candidates/AffinityCheck.js';
import { knowAnyoneText, knowAnyoneTip } from '../dealroom/format.js';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';
import { Switch } from '../ui/Switch.js';
import { useToast } from '../ui/Toast.js';
import { type SuggestScope, type SuggestStage, useSuggestion } from './useSuggestion.js';

const STAGE_OPTIONS: readonly { value: SuggestStage | ''; label: string }[] = [
  { value: '', label: 'The next stage after its latest round' },
  { value: 'pre_seed', label: 'Pre-seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'series_c_plus', label: 'Series C+' },
];

const SCOPE_OPTIONS: readonly { value: SuggestScope; label: string }[] = [
  { value: 'both', label: 'Both: tracked funds and ones we do not track yet' },
  { value: 'tracked', label: 'Tracked: the funds on our list' },
  { value: 'discover', label: 'Discover: Not tracked and Discovered funds' },
];

const NEW_LIST = 'new';

/** A row's own id: the Discovered fund's, else the fund's. */
const rowKey = (row: SuggestionRow): string => row.candidateId ?? row.fundId ?? row.name;

/** A Discovered fund nobody has tracked yet: adding it to a list tracks it first. */
const untracked = (row: SuggestionRow): boolean => row.candidate !== null && row.fundId === null;

export interface SuggestDrawerProps {
  /** The list the answer is for: rows on it are marked, and "Add" adds to it. */
  listId: string | null;
  /** The portfolio company to start from, from the list or the pane. */
  companyId: string | null;
  /** The pane's wording: "Suggest follow-on investors". */
  title: string;
  onClose: () => void;
}

/**
 * The agent's first pass at a list: pick the company (or describe one), the
 * stage and who to suggest from, run, and tick which of its suggestions go on
 * the list. Nothing is added, and nothing tracked, until a person says so.
 */
export function SuggestDrawer(props: SuggestDrawerProps): React.JSX.Element {
  const trpc = useTRPC();
  const titleId = useId();
  const companies = useQuery(trpc.portfolio.list.queryOptions());
  const [companyId, setCompanyId] = useState(props.companyId ?? '');
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState<SuggestStage | ''>('');
  const [scope, setScope] = useState<SuggestScope>('both');
  const [includeCorporates, setIncludeCorporates] = useState(false);
  const suggestion = useSuggestion({ listId: props.listId, companyId: props.companyId });
  const { onClose } = props;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const canRun =
    !suggestion.running &&
    !suggestion.isStarting &&
    (companyId !== '' || description.trim() !== '');
  const companyName =
    (companies.data ?? []).find((company) => company.id === companyId)?.name ?? null;

  return createPortal(
    <div
      className="drawer-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="suggest-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="panel-head">
          <div className="panel-title">
            <h2 id={titleId}>{props.title}</h2>
            <p className="subtle">
              An agent reads our funds, rounds, contacts and co-investments and proposes who to
              introduce. Nothing goes on a list, and nothing is tracked, until you add it.
            </p>
          </div>
          <button className="button small" type="button" onClick={onClose}>
            Close
          </button>
        </header>

        <form
          className="suggest-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canRun) return;
            suggestion.start({
              portfolioCompanyId: companyId === '' ? null : companyId,
              description,
              stage: stage === '' ? null : stage,
              scope,
              includeCorporates,
            });
          }}
        >
          <label className="suggest-field">
            <span className="subtle">Company</span>
            <Select
              label="Portfolio company"
              value={companyId}
              onChange={setCompanyId}
              options={[
                { value: '', label: 'Not in the portfolio — describe it below' },
                ...(companies.data ?? []).map((company) => ({
                  value: company.id,
                  label: company.name,
                })),
              ]}
            />
          </label>
          <label className="suggest-field">
            <span className="subtle">
              {companyId === '' ? 'What the company does' : 'Anything to add (optional)'}
            </span>
            <textarea
              rows={3}
              value={description}
              placeholder={
                companyId === ''
                  ? 'What it builds, for whom, where it is based, how far along it is.'
                  : 'What this round is for, who to avoid, what matters to the founder.'
              }
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label className="suggest-field">
            <span className="subtle">Stage being raised</span>
            <Select
              label="Stage being raised"
              value={stage}
              onChange={(value) =>
                setStage(STAGE_OPTIONS.find((o) => o.value === value)?.value ?? '')
              }
              options={STAGE_OPTIONS}
            />
          </label>
          <label className="suggest-field">
            <span className="subtle">Suggest from</span>
            <Select
              label="Suggest from"
              value={scope}
              onChange={(value) =>
                setScope(SCOPE_OPTIONS.find((o) => o.value === value)?.value ?? 'both')
              }
              options={SCOPE_OPTIONS}
            />
          </label>
          {scope !== 'tracked' && (
            <div className="suggest-field">
              <Switch checked={includeCorporates} onChange={setIncludeCorporates}>
                Include corporate investors
              </Switch>
            </div>
          )}
          <div className="suggest-run">
            <button className="button primary" type="submit" disabled={!canRun}>
              {suggestion.running ? 'Running…' : suggestion.shown === null ? 'Run' : 'Run again'}
            </button>
            <span className="subtle">
              Up to 15 lookups, about a minute; counts against today’s research ceiling.
              {scope !== 'tracked' &&
                ' Starts from a shortlist of funds we do not track, ranked from our rounds at no cost.'}
            </span>
          </div>
        </form>

        <Progress current={suggestion.current} running={suggestion.running} />

        {suggestion.shown !== null && (
          <Results
            key={suggestion.shown.jobId}
            job={suggestion.shown}
            listId={props.listId}
            companyId={companyId === '' ? null : companyId}
            companyName={suggestion.shown.companyName ?? companyName}
          />
        )}
      </aside>
    </div>,
    document.body,
  );
}

function Progress(props: {
  current: SuggestionJob | null;
  running: boolean;
}): React.JSX.Element | null {
  const { current } = props;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!props.running) return;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [props.running]);
  if (current === null) return null;
  if (props.running) {
    const since = Date.parse(current.startedAt ?? current.createdAt);
    const seconds = Math.max(0, Math.round((Date.now() - since) / 1000));
    return (
      <div className="suggest-progress" role="status">
        <div className="suggest-progress-bar" />
        <p className="subtle">
          {current.status === 'queued'
            ? 'Waiting for the worker…'
            : `Working through our data… ${seconds}s`}
        </p>
      </div>
    );
  }
  if (current.status === 'failed' || current.status === 'cancelled') {
    return (
      <p className="suggest-error">The last run failed: {current.error ?? 'no reason recorded'}</p>
    );
  }
  return null;
}

function Results(props: {
  job: SuggestionJob;
  listId: string | null;
  companyId: string | null;
  companyName: string | null;
}): React.JSX.Element {
  const { job } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        job.suggestions
          // A Discovered fund is ticked by a person: adding it tracks it.
          .filter((row) => row.conflict === null && !row.inList && !untracked(row))
          .map(rowKey),
      ),
  );
  const lists = useQuery(
    trpc.lists.list.queryOptions(undefined, { enabled: props.listId === null }),
  );
  const companyLists = (lists.data ?? []).filter(
    (list) => props.companyId !== null && list.portfolioCompanyId === props.companyId,
  );
  const [target, setTarget] = useState<string>(NEW_LIST);

  const add = useMutation(trpc.suggest.addToList.mutationOptions());
  const create = useMutation(trpc.lists.create.mutationOptions());
  const busy = add.isPending || create.isPending;
  const chosen = job.suggestions.filter((row) => selected.has(rowKey(row)) && !row.inList);
  const chosenToTrack = chosen.filter(untracked);
  const selectedPending = job.suggestions.flatMap((row) =>
    row.candidate?.status === 'pending' && row.candidateId !== null && selected.has(rowKey(row))
      ? [row.candidateId]
      : [],
  );
  const anyPending = job.suggestions.some((row) => row.candidate?.status === 'pending');
  const { checking } = useCandidateCheck();

  function refresh(listId: string | null): void {
    if (listId !== null) {
      void queryClient.invalidateQueries({ queryKey: trpc.lists.get.queryKey({ id: listId }) });
    }
    void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.suggest.status.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.suggest.latestFor.queryKey() });
  }

  async function addRows(rows: readonly SuggestionRow[]): Promise<void> {
    const fundIds = rows.flatMap((row) => (row.fundId === null ? [] : [row.fundId]));
    const candidateIds = rows.flatMap((row) =>
      untracked(row) && row.candidateId !== null ? [row.candidateId] : [],
    );
    try {
      let listId = props.listId ?? (target === NEW_LIST ? null : target);
      if (listId === null) {
        const name = `${props.companyName ?? 'New company'} — follow-on`;
        listId = (
          await create.mutateAsync({
            name,
            fundIds: [],
            ...(props.companyId === null ? {} : { portfolioCompanyId: props.companyId }),
          })
        ).id;
      }
      const outcome = await add.mutateAsync({ jobId: job.jobId, listId, fundIds, candidateIds });
      refresh(listId);
      const tracked =
        outcome.tracked === 0
          ? ''
          : ` Tracked ${outcome.tracked} Discovered fund${outcome.tracked === 1 ? '' : 's'}.`;
      toast(`Added ${outcome.added} fund${outcome.added === 1 ? '' : 's'} to the list.${tracked}`, {
        tone: 'info',
      });
      if (props.listId === null) navigate(`/lists/${listId}`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Adding to the list did not work.');
    }
  }

  const toggle = (key: string): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section className="suggest-results">
      <p className="subtle">
        {job.suggestions.length} investor{job.suggestions.length === 1 ? '' : 's'}
        {props.companyName === null ? '' : ` for ${props.companyName}`}
        {job.shortlistSize === null ? '' : ` · from a shortlist of ${job.shortlistSize}`} ·{' '}
        {job.toolCalls} lookups
        {job.costUsd === null ? '' : ` · $${job.costUsd.toFixed(2)}`}
        {job.finishedAt === null ? '' : ` · ${new Date(job.finishedAt).toLocaleString()}`}
      </p>
      <table className="suggest-table">
        <thead>
          <tr>
            <th aria-label="Select" />
            <th>Investor</th>
            <th>Why</th>
            <th>Confidence</th>
            <th>Intro</th>
          </tr>
        </thead>
        <tbody>
          {job.suggestions.map((row) => (
            <SuggestionLine
              key={rowKey(row)}
              row={row}
              checked={row.inList || selected.has(rowKey(row))}
              onToggle={() => toggle(rowKey(row))}
              busy={busy}
              onTrackAndAdd={() => void addRows([row])}
              checking={row.candidateId !== null && checking.has(row.candidateId)}
            />
          ))}
        </tbody>
      </table>
      <div className="suggest-actions">
        {anyPending && (
          <AffinityCheckButton
            selection={
              selectedPending.length === 0 ? null : { kind: 'ids', candidateIds: selectedPending }
            }
            onStarted={() => undefined}
            onFinished={() => refresh(null)}
          />
        )}
        {props.listId === null && (
          <Select
            label="Which list"
            value={target}
            onChange={setTarget}
            options={[
              { value: NEW_LIST, label: `Create list for ${props.companyName ?? 'this company'}` },
              ...companyLists.map((list) => ({ value: list.id, label: `Add to ${list.name}` })),
            ]}
          />
        )}
        <button
          className="button primary"
          type="button"
          disabled={chosen.length === 0 || busy}
          onClick={() => void addRows(chosen)}
        >
          {props.listId === null && target === NEW_LIST
            ? `Create list with ${chosen.length} selected`
            : `Add ${chosen.length} selected to list`}
          {chosenToTrack.length === 0 ? '' : ` (tracks ${chosenToTrack.length})`}
        </button>
      </div>
    </section>
  );
}

function SuggestionLine(props: {
  row: SuggestionRow;
  checked: boolean;
  onToggle: () => void;
  busy: boolean;
  onTrackAndAdd: () => void;
  /** The Affinity check under way is still to answer for it. */
  checking: boolean;
}): React.JSX.Element {
  const { row } = props;
  const percent = Math.round(row.confidence * 100);
  const affinity = row.candidate?.affinity ?? null;
  const know = affinity === null ? null : knowAnyoneText(affinity);
  return (
    <tr className={row.conflict === null ? undefined : 'suggest-conflicted'}>
      <td>
        <input
          type="checkbox"
          aria-label={`Select ${row.name}`}
          checked={props.checked}
          disabled={row.inList}
          onChange={props.onToggle}
        />
      </td>
      <td className="suggest-fund">
        {row.fundId === null ? row.name : <Link href={`/?fund=${row.fundId}`}>{row.name}</Link>}
        {row.inList && <span className="tag">In list</span>}
        {untracked(row) && <span className="tag">Discovered</span>}
        {row.listStatus === 'pool' && <span className="tag">Not tracked</span>}
        {row.candidate !== null && (
          <p className="subtle suggest-meta">
            {row.candidate.roundsSeen} round{row.candidate.roundsSeen === 1 ? '' : 's'} on file
            {row.website !== null && (
              <>
                {' · '}
                <a href={row.website} target="_blank" rel="noreferrer">
                  website
                </a>
              </>
            )}
          </p>
        )}
        {untracked(row) && !row.inList && (
          <button
            className="button small"
            type="button"
            disabled={props.busy}
            onClick={props.onTrackAndAdd}
          >
            Track and add to list
          </button>
        )}
      </td>
      <td>
        {row.reason}
        {row.conflict !== null && (
          <p className="suggest-conflict">
            Conflict{row.conflict.partialPortfolio ? ' (partial portfolio)' : ''}:{' '}
            {row.conflict.companyName} — {row.conflict.reason}
          </p>
        )}
      </td>
      <td>
        <div className="confidence" title={`${percent}%`}>
          <div className="confidence-fill" style={{ width: `${percent}%` }} />
        </div>
        <span className="subtle">{percent}%</span>
      </td>
      <td className="subtle">
        {affinity !== null && props.checking ? (
          <span>Know anyone? Checking…</span>
        ) : affinity !== null && know !== null ? (
          <span title={knowAnyoneTip(affinity) ?? undefined}>
            Know anyone? {know.text} {know.faint}
          </span>
        ) : row.suggestedMemberName === null ? (
          (row.suggestedInvestorName ?? '—')
        ) : (
          `${row.suggestedMemberName} → ${row.suggestedInvestorName ?? 'someone there'}`
        )}
      </td>
    </tr>
  );
}
